import React from "react";
import { motion } from "framer-motion";
import {
  Grid,
  Container,
  Typography,
  Box,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Paper,
  Chip,
  Avatar,
  AvatarGroup,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Public,
  LocationOn,
  Info,
  Groups,
  School,
  Link as LinkIcon,
  VolunteerActivism,
  Home,
  Map,
  LibraryBooks,
  ContactSupport,
  ArrowForward,
  CheckCircle,
  Download,
  GitHub,
  Handshake,
  Person,
  Business,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import HomeRefreshButton from "../buttons/HomeRefreshButton";
import logo from "../../assets/Foundation_Lachezar_Tsotsorkov_Logo_BG_Blue.png";
import ipiLogo from "../../assets/ipi-logo.svg"; // Импортиране на ИПИ логото

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery("(max-width:600px)");

  // Responsive spacing values
  const getSpacing = () => {
    if (isSmall) return 2;
    if (isMobile) return 3;
    return 4;
  };

  // Responsive typography variants
  const getTitleVariant = () => {
    if (isSmall) return "h4";
    if (isMobile) return "h3";
    return "h2";
  };

  const getSectionTitleVariant = () => {
    if (isSmall) return "h6";
    if (isMobile) return "h5";
    return "h5";
  };

  const volunteers = [
    "Евгения Сарафова",
    "Емилиян Кадийски",
    "Калоян Васев",
    "Магдалена Румбалт",
  ];

  return (
    <>
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 4, sm: 5, md: 6 },
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Фондация текст и лого */}
        <Box
          sx={{
            mb: { xs: 4, md: 5 },
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            bgcolor: "rgba(33, 150, 243, 0.05)",
            border: "1px solid rgba(33, 150, 243, 0.2)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center", // Променено на center за вертикално центриране
            justifyContent: "center",
            gap: { xs: 2, md: 3 },
            textAlign: { xs: "center", md: "left" }, // Добавено
          }}
        >
          {/* Лого */}
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={logo}
              alt="Фондация Лъчезар Цоцорков"
              style={{
                width: isSmall ? "180px" : isMobile ? "220px" : "280px", // По-малки размери за PNG лого
                height: "auto",
                maxWidth: "100%",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Текст */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#455a64",
                lineHeight: 1.6,
                fontSize: { xs: "0.85rem", sm: "1rem" },
                textAlign: "justify",
              }}
            >
              Проектът „Изследване-мапинг на читалищната мрежа в България" е
              съфинансиран от фондация „Лъчезар Цоцорков". Изявленията и
              мненията, изразени тук, принадлежат единствено на фондация
              „Народни читалища" и не отразяват непременно вижданията на
              фондация „Лъчезар Цоцорков" или нейните партньори.
            </Typography>
          </Box>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Заглавие */}
          <Box
            sx={{
              textAlign: "center",
              mb: { xs: 4, md: 6 },
              px: { xs: 1, sm: 2 },
            }}
          >
            <Public
              sx={{
                fontSize: { xs: 48, sm: 56, md: 60 },
                color: "#1976d2",
                mb: 2,
              }}
            />

            <Typography
              variant={getTitleVariant()}
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "#1a237e",
                mb: 3,
                lineHeight: 1.2,
                textAlign: { xs: "center", sm: "center" },
                hyphens: "none",
                overflowWrap: "break-word",
              }}
            >
              Първата онлайн карта на народните читалища в България
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                color: "text.secondary",
                mb: 3,
                maxWidth: "800px",
                mx: "auto",
                textAlign: { xs: "justify", sm: "justify" },
                textAlignLast: { xs: "left", sm: "center" },
                lineHeight: 1.7,
              }}
            >
              Интерактивна платформа за изследване и визуализация на близо 3600
              читалища в цяла България
            </Typography>

            <Divider
              sx={{
                my: { xs: 3, md: 4 },
                mx: "auto",
                width: { xs: "80px", md: "100px" },
                borderWidth: 2,
                borderColor: "#1976d2",
              }}
            />
          </Box>

          {/* Основни характеристики */}
          <Grid container spacing={getSpacing()} sx={{ mb: { xs: 4, md: 6 } }}>
            <Grid item xs={12} md={4}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 1,
                        borderRadius: "12px",
                        bgcolor: "#1976d2",
                        color: "white",
                      }}
                    >
                      <Map />
                    </Box>
                    <Typography
                      variant={getSectionTitleVariant()}
                      sx={{ color: "#1a237e" }}
                    >
                      Географска визуализация
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#455a64",
                      textAlign: "justify",
                      lineHeight: 1.7,
                    }}
                  >
                    Интерактивна карта показваща разпределението на близо 3600
                    читалища в цяла България с възможност за търсене и
                    филтриране по различни критерии.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 1,
                        borderRadius: "12px",
                        bgcolor: "#2e7d32",
                        color: "white",
                      }}
                    >
                      <LibraryBooks />
                    </Box>
                    <Typography
                      variant={getSectionTitleVariant()}
                      sx={{ color: "#1a237e" }}
                    >
                      Подробна информация
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#455a64",
                      textAlign: "justify",
                      lineHeight: 1.7,
                    }}
                  >
                    Всеки читалищен профил съдържа пълна информация за адрес,
                    контакти, дейности, персонал, проекти и исторически данни.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 1,
                        borderRadius: "12px",
                        bgcolor: "#ed6c02",
                        color: "white",
                      }}
                    >
                      <ContactSupport />
                    </Box>
                    <Typography
                      variant={getSectionTitleVariant()}
                      sx={{ color: "#1a237e" }}
                    >
                      Отворени данни
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#455a64",
                      textAlign: "justify",
                      lineHeight: 1.7,
                    }}
                  >
                    Всички данни са публично достъпни и могат да се използват за
                    изследвания, анализи и приложения с некомерсиална цел.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Основен текст */}
          <Grid container spacing={getSpacing()} sx={{ mb: { xs: 4, md: 6 } }}>
            <Grid item xs={12}>
              <Card
                elevation={3}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                  <Typography
                    variant="body1"
                    paragraph
                    sx={{
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      lineHeight: 1.8,
                      color: "#424242",
                      mb: 4,
                      textAlign: "justify",
                      textAlignLast: "left",
                    }}
                  >
                    Този сайт представя{" "}
                    <strong>
                      Първата онлайн карта на народните читалища в България
                    </strong>
                    . Картата събира на едно място информация за близо 3600
                    читалища в цялата страна и дава ясен поглед върху тяхното
                    разположение, разпространение и основни характеристики. Тя е
                    създадена като публичен инструмент за ориентиране, сравнение
                    и по-добро разбиране на мащаба и разнообразието на
                    читалищната мрежа.
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          height: "100%",
                          bgcolor: "#f8f9fa",
                          borderLeft: "4px solid #2e7d32",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="flex-start"
                        >
                          <LocationOn
                            sx={{
                              color: "#2e7d32",
                              mt: 0.5,
                              fontSize: { xs: 24, md: 28 },
                            }}
                          />
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                lineHeight: 1.8,
                                color: "#424242",
                                textAlign: "justify",
                                textAlignLast: "left",
                              }}
                            >
                              Всяка точка на картата води към собствена страница
                              с подробна информация за конкретното читалище,
                              включително основни данни (адрес, контакти, име,
                              година на основаване и други), информация за
                              дейностите му през годините, културни и
                              образователни инициативи, участие в проекти и
                              данни за персонала.
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          height: "100%",
                          bgcolor: "#f8f9fa",
                          borderLeft: "4px solid #ed6c02",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="flex-start"
                        >
                          <Groups
                            sx={{
                              color: "#ed6c02",
                              mt: 0.5,
                              fontSize: { xs: 24, md: 28 },
                            }}
                          />
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                lineHeight: 1.8,
                                color: "#424242",
                                textAlign: "justify",
                                textAlignLast: "left",
                              }}
                            >
                              Картата е предназначена както за институции,
                              изследователи и медии, така и за самите читалища и
                              за широката общественост, като създава основа за
                              по-добра видимост, информирани решения и бъдещо
                              надграждане с актуални данни и нови
                              функционалности.
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      mt: 4,
                      p: { xs: 2.5, md: 3 },
                      borderRadius: 2,
                      bgcolor: "#fff8e1",
                      border: "1px solid #ffd54f",
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <School
                        sx={{
                          color: "#9c27b0",
                          mt: 0.5,
                          fontSize: { xs: 24, md: 28 },
                        }}
                      />
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.8,
                            color: "#424242",
                            textAlign: "justify",
                            textAlignLast: "left",
                          }}
                        >
                          В допълнение, обработена и анализирана информация за
                          всички читалища в България, включително
                          систематизирана информация по общини, може да бъде
                          намерена на{" "}
                          <a
                            href="https://danni.narodnichitalishta.bg/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#1976d2",
                              fontWeight: 600,
                              textDecoration: "none",
                              borderBottom: "2px solid #1976d2",
                              transition: "all 0.3s ease",
                              whiteSpace: "nowrap",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.color = "#1565c0";
                              e.target.style.borderBottomColor = "#1565c0";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.color = "#1976d2";
                              e.target.style.borderBottomColor = "#1976d2";
                            }}
                          >
                            https://danni.narodnichitalishta.bg/
                          </a>
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Кому е полезна картата */}
          <Card
            elevation={3}
            sx={{
              mb: { xs: 4, md: 6 },
              borderRadius: 3,
              background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Typography
                variant={getSectionTitleVariant()}
                component="h2"
                sx={{
                  fontWeight: 600,
                  color: "#1a237e",
                  mb: 3,
                  textAlign: "center",
                }}
              >
                За кого е полезна тази карта?
              </Typography>

              <Grid container spacing={2}>
                {[
                  {
                    title: "Изследователи и академици",
                    description:
                      "За проучвания и анализи в областта на културата, образованието и общественото развитие",
                    color: "#1976d2",
                  },
                  {
                    title: "Читалищни дейци и мениджъри",
                    description:
                      "За установяване на контакти, обмен на добри практики и сътрудничество",
                    color: "#2e7d32",
                  },
                  {
                    title: "Общинска администрация",
                    description:
                      "За планиране и развитие на културната политика на местно ниво",
                    color: "#ed6c02",
                  },
                  {
                    title: "Медии и журналисти",
                    description:
                      "За достъп до точна информация и контакти с читалища",
                    color: "#9c27b0",
                  },
                  {
                    title: "НПО и фондации",
                    description:
                      "За насочване на проекти и инициативи към конкретни читалища",
                    color: "#d32f2f",
                  },
                  {
                    title: "Граждани и доброволци",
                    description:
                      "За откриване на читалищни дейности и възможности за участие",
                    color: "#7b1fa2",
                  },
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper
                      sx={{
                        p: 2,
                        height: "100%",
                        borderRadius: 2,
                        bgcolor: "white",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1.5,
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: item.color,
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: item.color }}
                        >
                          {item.title}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#455a64",
                          textAlign: "justify",
                          lineHeight: 1.6,
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Благодарности */}
          {/* Благодарности */}
          <Card
            elevation={3}
            sx={{
              mb: { xs: 4, md: 6 },
              borderRadius: 3,
              background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    p: 2,
                    borderRadius: "50%",
                    bgcolor: "#d32f2f",
                    color: "white",
                    mb: 2,
                  }}
                >
                  <VolunteerActivism sx={{ fontSize: { xs: 40, md: 50 } }} />
                </Box>
                <Typography
                  variant={getSectionTitleVariant()}
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    color: "#1a237e",
                    mb: 2,
                  }}
                >
                  Създадена с подкрепата и благодарение на:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#455a64",
                    mb: 3,
                    maxWidth: "600px",
                    mx: "auto",
                    textAlign: "justify",
                    textAlignLast: "center",
                  }}
                >
                  Тази платформа е резултат от съвместни усилия и желание за
                  създаване на публичен ресурс за всички заинтересовани от
                  развитието на читалищното дело в България.
                </Typography>
              </Box>

              <Grid container spacing={getSpacing()}>
                {/* Фондация Лъчезар Цоцорков */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2.5, md: 3 },
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          p: 2,
                          borderRadius: "16px",
                          bgcolor: "#1976d2",
                          color: "white",
                          mb: 2,
                        }}
                      >
                        <Handshake sx={{ fontSize: { xs: 32, md: 40 } }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#1976d2",
                          mb: 2,
                        }}
                      >
                        Фондация "Лъчезар Цоцорков"
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexGrow: 1,
                        }}
                      >
                        <img
                          src={logo}
                          alt="Фондация Лъчезар Цоцорков"
                          style={{
                            width: isSmall
                              ? "180px"
                              : isMobile
                              ? "2200px"
                              : "260px",
                            height: "auto",
                            maxWidth: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#455a64",
                          textAlign: "justify",
                          textAlignLast: "center",
                          flexGrow: 1,
                        }}
                      >
                        Основен партньор и финансова подкрепа за реализацията на
                        проекта.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Партньори */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2.5, md: 3 },
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          p: 2,
                          borderRadius: "16px",
                          bgcolor: "#9c27b0",
                          color: "white",
                          mb: 2,
                        }}
                      >
                        <Business sx={{ fontSize: { xs: 32, md: 40 } }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#9c27b0",
                          mb: 2,
                        }}
                      >
                        Партньори
                      </Typography>

                      {/* ИПИ Лого */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexGrow: 1,
                        }}
                      >
                        <img
                          src={ipiLogo}
                          alt="ИПИ - Институт за публични инициативи"
                          style={{
                            width: isSmall
                              ? "140px"
                              : isMobile
                              ? "160px"
                              : "180px",
                            height: "auto",
                            maxWidth: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#455a64",
                          textAlign: "center",
                          mt: 2,
                        }}
                      >
                        Стратегически партньор за развитието на проекта
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Доброволен труд */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2.5, md: 3 },
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          p: 2,
                          borderRadius: "16px",
                          bgcolor: "#2e7d32",
                          color: "white",
                          mb: 2,
                        }}
                      >
                        <Person sx={{ fontSize: { xs: 32, md: 40 } }} />
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#2e7d32",
                          mb: 2,
                        }}
                      >
                        Доброволен труд
                      </Typography>

                      {/* Доброволци */}
                      <Box
                        sx={{
                          flexGrow: 1,
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                      >
                        <List dense>
                          {volunteers.map((volunteer, index) => (
                            <ListItem
                              key={index}
                              sx={{
                                py: 0.5,
                                px: 0,
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              {/* <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircle
                                  sx={{ color: "#2e7d32", fontSize: 16 }}
                                />
                              </ListItemIcon> */}
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#455a64",
                                      textAlign: "center",
                                    }}
                                  >
                                    {volunteer}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#455a64",
                          textAlign: "center",
                          mt: 2,
                        }}
                      >
                        Специалисти, дали своя принос за реализацията
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Допълнителни ресурси */}
          <Card
            elevation={2}
            sx={{
              mb: { xs: 4, md: 6 },
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Typography
                variant={getSectionTitleVariant()}
                component="h2"
                sx={{
                  fontWeight: 600,
                  color: "#1a237e",
                  mb: 3,
                  textAlign: "center",
                }}
              >
                Допълнителни ресурси и връзки
              </Typography>

              <Grid container spacing={2}>
                {[
                  {
                    title: "Подаване на обратна връзка",
                    description: "Сигнализиране за неточности или допълнения",
                    icon: <ContactSupport />,
                    href: "/feedback",
                    color: "#ed6c02",
                  },
                  {
                    title: "Методология",
                    description: "Как са събирани и обработвани данните",
                    icon: <Info />,
                    href: "/methodology",
                    color: "#9c27b0",
                  },
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Button
                      component={item.href.startsWith("http") ? "a" : Link}
                      href={
                        item.href.startsWith("http") ? item.href : undefined
                      }
                      to={!item.href.startsWith("http") ? item.href : undefined}
                      target={
                        item.href.startsWith("http") ? "_blank" : undefined
                      }
                      rel={
                        item.href.startsWith("http")
                          ? "noopener noreferrer"
                          : undefined
                      }
                      fullWidth
                      variant="outlined"
                      startIcon={item.icon}
                      endIcon={<ArrowForward />}
                      sx={{
                        justifyContent: "space-between",
                        p: 2.5,
                        borderRadius: 2,
                        borderColor: `${item.color}40`,
                        color: item.color,
                        "&:hover": {
                          borderColor: item.color,
                          bgcolor: `${item.color}10`,
                          transform: "translateX(4px)",
                        },
                        transition: "all 0.3s ease",
                        height: "100%",
                      }}
                    >
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Бутони за навигация */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              flexWrap: "wrap",
              mt: { xs: 4, md: 6 },
              pt: { xs: 3, md: 4 },
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <HomeRefreshButton />
          </Box>
        </motion.div>
      </Container>
    </>
  );
};

export default About;
